import { useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import { useAppNavigation } from './useAppNavigation';
import { Folder, AudioFile } from '../types';
import { notificationService } from '../services/notifications';

interface DashboardState {
  folders: Folder[];
  files: AudioFile[];
  allFiles: AudioFile[];
  currentFolderId: string | null;
  setFolders: (folders: Folder[] | ((prev: Folder[]) => Folder[])) => void;
  setFiles: (files: AudioFile[] | ((prev: AudioFile[]) => AudioFile[])) => void;
  setAllFiles: (
    files: AudioFile[] | ((prev: AudioFile[]) => AudioFile[])
  ) => void;
  setCurrentFolderId: (id: string | null) => void;
  loadFiles: () => Promise<void>;
  loadAllFiles: () => Promise<void>;
  updateFolderStatistics: (updatedAllFiles: AudioFile[]) => void;
  setCurrentTranscript: (transcript: any) => void;
}

interface EventHandlers {
  // File events
  onFileCreated: (file: AudioFile) => void;
  onFileUpdated: (file: AudioFile) => void;
  onFileDeleted: (fileId: string) => void;
  onFileMoved: (
    fileId: string,
    fromFolderId: string | null,
    toFolderId: string | null
  ) => void;

  // Folder events
  onFolderCreated: (folder: Folder) => void;
  onFolderUpdated: (folderId: string, updatedFolder: Folder) => void;
  onFolderDeleted: (folderId: string) => void;
  onFolderSelected: (folderId: string | null) => void;

  // Upload events
  onUploadStarted: (files: FileList) => void;
  onUploadCompleted: (file: AudioFile) => void;
  onUploadError: (error: string) => void;
}

export function useDashboardEvents(
  dashboardState: DashboardState,
  setUploadState: {
    setIsUploading: (uploading: boolean) => void;
  }
): EventHandlers {
  const { state } = useApp();
  const { selectAudioFile, selectFolder } = useAppNavigation();
  const {
    folders,
    allFiles,
    currentFolderId,
    setFolders,
    setFiles,
    setAllFiles,
    setCurrentFolderId,
    loadFiles,
    loadAllFiles,
    updateFolderStatistics,
    setCurrentTranscript,
  } = dashboardState;

  // Generic file list update function
  const updateFileLists = useCallback(
    (
      updater: (files: AudioFile[]) => AudioFile[],
      skipFolderStatsUpdate = false
    ) => {
      let updatedAllFiles: AudioFile[];

      setFiles((currentFiles) => updater(currentFiles));
      setAllFiles((currentAllFiles) => {
        updatedAllFiles = updater(currentAllFiles);

        // Update folder statistics with the new files (outside of setState)
        // Skip if explicitly requested (e.g., during upload completion when switching to file view)
        if (!skipFolderStatsUpdate) {
          setTimeout(() => {
            updateFolderStatistics(updatedAllFiles);
          }, 0);
        }

        return updatedAllFiles;
      });
    },
    [setFiles, setAllFiles, updateFolderStatistics]
  );

  // Generic selected state cleanup
  const clearSelectedIfMatches = useCallback(
    (fileId?: string, folderId?: string) => {
      if (fileId && state.selectedAudioFile?.id === fileId) {
        selectAudioFile(null);
        setCurrentTranscript(null);
      }
      if (folderId && state.selectedFolder?.id === folderId) {
        selectFolder(null);
      }
    },
    [
      state.selectedAudioFile,
      state.selectedFolder,
      selectAudioFile,
      selectFolder,
      setCurrentTranscript,
    ]
  );

  // File event handlers
  const onFileCreated = useCallback(
    (file: AudioFile, skipFolderStatsUpdate = false) => {
      updateFileLists((files) => [...files, file], skipFolderStatsUpdate);
      loadFiles();
      loadAllFiles();
    },
    [updateFileLists, loadFiles, loadAllFiles]
  );

  const onFileUpdated = useCallback(
    (updatedFile: AudioFile) => {
      updateFileLists((files) =>
        files.map((f) => (f.id === updatedFile.id ? updatedFile : f))
      );

      // Update selected file if it's currently selected
      if (state.selectedAudioFile?.id === updatedFile.id) {
        selectAudioFile(updatedFile);
      }
    },
    [updateFileLists, state.selectedAudioFile, selectAudioFile]
  );

  const onFileDeleted = useCallback(
    (deletedFileId: string) => {
      clearSelectedIfMatches(deletedFileId);
      updateFileLists((files) => files.filter((f) => f.id !== deletedFileId));
    },
    [clearSelectedIfMatches, updateFileLists]
  );

  const onFileMoved = useCallback(
    (
      fileId: string,
      fromFolderId: string | null,
      toFolderId: string | null
    ) => {
      updateFileLists((files) =>
        files.map((f) => (f.id === fileId ? { ...f, folderId: toFolderId } : f))
      );
    },
    [updateFileLists]
  );

  // Folder event handlers
  const onFolderCreated = useCallback(
    (folder: Folder) => {
      setFolders((prev) => [...(prev || []), folder]);
    },
    [setFolders]
  );

  const onFolderUpdated = useCallback(
    (folderId: string, updatedFolder: Folder) => {
      setFolders((prev) =>
        (prev || []).map((f) => (f.id === folderId ? updatedFolder : f))
      );

      // Update selected folder if it's currently selected
      if (state.selectedFolder?.id === folderId) {
        const folderFiles = allFiles.filter((f) => f.folderId === folderId);
        const folderWithStats = {
          ...updatedFolder,
          fileCount: folderFiles.length,
          totalSize: folderFiles.reduce((sum, f) => sum + (f.fileSize || 0), 0),
          transcribedCount: folderFiles.filter(
            (f) => f.hasTranscript && f.status === 'ready'
          ).length,
        };
        selectFolder(folderWithStats);
      }
    },
    [setFolders, state.selectedFolder, allFiles, selectFolder]
  );

  const onFolderDeleted = useCallback(
    (folderId: string) => {
      clearSelectedIfMatches(undefined, folderId);
      setFolders((prev) => (prev || []).filter((f) => f.id !== folderId));
      if (currentFolderId === folderId) {
        setCurrentFolderId(null);
      }
    },
    [clearSelectedIfMatches, setFolders, currentFolderId, setCurrentFolderId]
  );

  const onFolderSelected = useCallback(
    (folderId: string | null) => {
      setCurrentFolderId(folderId);

      if (folderId === null) {
        // Common folder (root)
        const commonFolder = {
          id: 'common',
          name: 'Common',
          createdAt: state.user?.created_at || new Date().toISOString(),
          fileCount: allFiles.filter((f) => !f.folderId).length,
          totalSize: allFiles
            .filter((f) => !f.folderId)
            .reduce((sum, f) => sum + (f.fileSize || 0), 0),
          transcribedCount: allFiles.filter(
            (f) => !f.folderId && f.hasTranscript && f.status === 'ready'
          ).length,
        };
        selectFolder(commonFolder);
      } else {
        const selectedFolder = folders.find((f) => f.id === folderId);
        if (selectedFolder) {
          const folderFiles = allFiles.filter((f) => f.folderId === folderId);
          const folderWithStats = {
            ...selectedFolder,
            fileCount: folderFiles.length,
            totalSize: folderFiles.reduce(
              (sum, f) => sum + (f.fileSize || 0),
              0
            ),
            transcribedCount: folderFiles.filter(
              (f) => f.hasTranscript && f.status === 'ready'
            ).length,
          };
          selectFolder(folderWithStats);
        }
      }
    },
    [
      setCurrentFolderId,
      selectFolder,
      allFiles,
      folders,
      state.user?.created_at,
    ]
  );

  // Upload event handlers
  const onUploadStarted = useCallback(
    (files: FileList) => {
      setUploadState.setIsUploading(true);
    },
    [setUploadState]
  );

  const onUploadCompleted = useCallback(
    (file: AudioFile) => {
      // Skip folder statistics update to prevent folder re-selection
      onFileCreated(file, true);
      notificationService.success(`Successfully uploaded ${file.filename}`);
      setUploadState.setIsUploading(false);

      // Auto-navigate to the uploaded file to show progress indicator
      selectAudioFile(file);
      setCurrentTranscript(null); // Clear any existing transcript
    },
    [onFileCreated, setUploadState, selectAudioFile, setCurrentTranscript]
  );

  const onUploadError = useCallback(
    (error: string) => {
      notificationService.error(`Upload failed: ${error}`);
      setUploadState.setIsUploading(false);
    },
    [setUploadState]
  );

  return {
    // File events
    onFileCreated,
    onFileUpdated,
    onFileDeleted,
    onFileMoved,

    // Folder events
    onFolderCreated,
    onFolderUpdated,
    onFolderDeleted,
    onFolderSelected,

    // Upload events
    onUploadStarted,
    onUploadCompleted,
    onUploadError,
  };
}
